'use client';

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import { DEFAULT_CURRENCY, isCustomerCurrency, type CustomerCurrency } from '@/config/currency';
import { writeBrowserCurrencyCookie } from '@/lib/currency/browser-cookie';

type CurrencyContextValue = {
  currentCurrency: CustomerCurrency;
  setCurrency: (currency: CustomerCurrency) => void;
};

const CurrencyContext = createContext<CurrencyContextValue | undefined>(undefined);

type CurrencyProviderProps = {
  children: ReactNode;
  /** Cookie-derived currency from the server to avoid SSR/client mismatch. */
  initialCurrency?: CustomerCurrency;
};

export const CurrencyProvider = ({
  children,
  initialCurrency = DEFAULT_CURRENCY,
}: CurrencyProviderProps) => {
  const [currentCurrency, setCurrentCurrency] = useState<CustomerCurrency>(() =>
    isCustomerCurrency(initialCurrency) ? initialCurrency : DEFAULT_CURRENCY
  );

  const setCurrency = useCallback((currency: CustomerCurrency) => {
    if (!isCustomerCurrency(currency)) {
      return;
    }

    writeBrowserCurrencyCookie(currency);
    setCurrentCurrency(currency);
  }, []);

  const value = useMemo(
    () => ({
      currentCurrency,
      setCurrency,
    }),
    [currentCurrency, setCurrency]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = (): CurrencyContextValue => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};
